/**
 * This file is part of vue-boilerplate.
 * @link     : https://zhaiyiming.com/
 * @author   : Emil Zhai (root@derzh.com)
 * @modifier : Emil Zhai (root@derzh.com)
 * @copyright: Copyright (c) 2018 TINYMINS.
 */
import * as api from '@/api/common';
import { InviteInfo, QrcodeData, JssdkConfig, SubmitException } from '@/api/types/common';
import store from '@/store';
import { COMMON, USER } from '@/store/types';
import { finalizeAction, ActionType, StoreActionEnv } from '@/store/actions';

import busModule, { StoreCommonBusState } from './bus';
import routeModule, { StoreCommonRouteState } from './route';
import startModule, { StoreCommonStartState } from './start';
import badgeModule, { StoreCommonBadgeState } from './badge';
import audioModule, { StoreCommonAudioState } from './audio';

export interface StoreCommonState {
  sharesInviteInfo: InviteInfo;
  qrcodes: Record<string, QrcodeData>;
  wechatSDKInfo: JssdkConfig;
  connectCode: string;
  connectService: string;
  connectResponse: { status: number; data: null };
  submitException: SubmitException[];
  submitExceptionPage: number;
  // modules
  bus: StoreCommonBusState;
  route: StoreCommonRouteState;
  start: StoreCommonStartState;
  badge: StoreCommonBadgeState;
  audio: StoreCommonAudioState;
}

export default {
  namespaced: true,
  modules: {
    bus: busModule,
    route: routeModule,
    start: startModule,
    badge: badgeModule,
    audio: audioModule,
  },
  state: {
    sharesInviteInfo: null,
    qrcodes: {},
    wechatSDKInfo: {},
    connectCode: null,
    connectService: null,
    connectResponse: null,
    submitException: [],
    submitExceptionPage: -1,
  },
  getters: {},
  actions: {
    [COMMON.UPLOAD_FILE](_, file) {
      return new Promise((resolve, reject) => {
        api.uploadFile(file).then((res) => {
          resolve(res.data);
        }).catch(reject);
      });
    },
    [COMMON.UPLOAD_BLOB](_, { blob, name }) {
      return new Promise((resolve, reject) => {
        api.uploadBlob(blob, name).then((res) => {
          resolve(res.data);
        }).catch(reject);
      });
    },
    [COMMON.GET_SHARES_INVITE_INFO]({ state, commit }, { action: rawAction, game }) {
      const action = finalizeAction(rawAction, state.sharesInviteInfoGame === game, state.sharesInviteInfo);
      if (action) {
        return new Promise((resolve, reject) => {
          api.getInviteInfo(game).then((res) => {
            commit(COMMON.GET_SHARES_INVITE_INFO, {
              game,
              inviteInfo: res.data,
            });
            resolve();
          }).catch(reject);
        });
      }
      return Promise.resolve();
    },
    [COMMON.GET_QRCODES]({ state, commit }, params) {
      if (!state.qrcodes[params.scene]) {
        params.reload = true;
      }
      if (params.reload) {
        return new Promise((resolve, reject) => {
          api.getQrcodes({ scene: params.scene }).then((res) => {
            commit(COMMON.GET_QRCODES, {
              scene: params.scene,
              data: res.data,
            });
            resolve();
          }).catch(reject);
        });
      }
      return Promise.resolve();
    },
    [COMMON.GET_WECHAT_SDK_INFO]({ state, commit }, { url }) {
      if (!state.wechatSDKInfo[url]) {
        return new Promise((resolve, reject) => {
          api.getWechatSDKInfo(url).then((res) => {
            commit(COMMON.GET_WECHAT_SDK_INFO, {
              url,
              info: res.data,
            });
            resolve(res.data);
          }).catch(reject);
        });
      }
      return Promise.resolve(state.wechatSDKInfo[url]);
    },
    [COMMON.CONNECT]({ state, commit }, { code, service = 'wx' }) {
      if (state.connectCode !== code || state.connectService !== service) {
        return new Promise((resolve, reject) => {
          api.connect({
            code,
            service,
          }).then((res) => {
            commit(COMMON.CONNECT, {
              code,
              service,
              response: { status: res.errcode, data: res.data },
            });
            store.dispatch(`user/${USER.GET}`, { action: 'refresh', silent: true }).then(() => resolve(res));
          }).catch(reject);
        });
      }
      return Promise.resolve(state.connectResponse);
    },
    [COMMON.GET_BANK_INFO](_, { card, silent }) {
      return api.getBankInfo(card, silent);
    },
    [COMMON.GET_SUBMIT_EXCEPTION](
      { state, commit }: StoreActionEnv<StoreCommonState>,
      { action: rawAction = '', page }: { action?: ActionType; page: number },
    ) {
      const action = finalizeAction(rawAction, state.submitExceptionPage === page && state.submitException.length);
      if (action) {
        return new Promise<void>((resolve, reject) => {
          api.getSubmitException().then((res) => {
            commit(COMMON.GET_SUBMIT_EXCEPTION, {
              page,
              data: res.data,
            });
            resolve();
          }).catch(reject);
        });
      }
      return Promise.resolve();
    },
    [COMMON.UPLOAD_INFO](_, { size, extension }) {
      return new Promise((resolve, reject) => {
        api.uploadInfo(size, extension).then((res) => {
          resolve(res.data);
        }).catch(reject);
      });
    },
  },
  mutations: {
    [COMMON.GET_SHARES_INVITE_INFO](state, { game, inviteInfo }) {
      state.sharesInviteInfoGame = game;
      state.sharesInviteInfo = inviteInfo;
    },
    [COMMON.GET_QRCODES](state, { scene, data }) {
      state.qrcodes[scene] = data;
    },
    [COMMON.GET_WECHAT_SDK_INFO](state, { url, info }) {
      state.wechatSDKInfo[url] = info;
    },
    [COMMON.CONNECT](state, { code, service, response }) {
      state.connectCode = code;
      state.connectService = service;
      state.connectResponse = response;
    },
    [COMMON.GET_SUBMIT_EXCEPTION](state, { page, data }: { page: number; data: SubmitException[] }) {
      state.submitException = data;
      state.submitExceptionPage = page;
    },
  },
};
